// Models
import mProduct from '../models/product';
// Custom
import { defaultPaginatedListResult } from './App';

/*
** From Webpack Env Config
*/
const PRODUCT = __PRODUCT__; // eslint-disable-line no-undef

const date = new Date();
const year = date.getFullYear();

// ------------------------------------- //
// Global config
// ------------------------------------- //
const staticConfig = {
  brand: 'OPAC',
  crCompany: 'Onepark',
  year,
  productLink: 'https://www.onepark.fr/',
  AutoCloseMobileNav: true,                         // true, false. Automatically close sidenav on route change (Mobile only)
};

// ------------------------------------- //
// Common reducers config
// ------------------------------------- //
const commonReducersConfig = {
  settings: {
    layoutBoxed: false,                             // true, false
    navCollapsed: false,                            // true, false
    navBehind: false,                               // true, false
    fixedHeader: true,                              // true, false
    sidebarWidth: 'middle',                         // small, middle, large
    colorOption: '21',                              // String: 11,12,13,14,15,16; 21,22,23,24,25,26; 31,32,33,34,35,36
    themeOpac: 'themeOpac',                         // light, gray, dark, onepark
    themeCpmManager: 'themeCpmManager',
    themeCpmClient: 'themeCpmClient',
  },
  version: {
    persistStore: 0,
  },
  persist: {
    persistor: null,
  },
  intl: {
    locale: '',
    language: '',
    countryCode: '',
    desc: '',
  },
  loading: {
    operation: false,
    processing: false,
    forceHide: false,
  },
  webSocket: {
    open: false,
    topics: [],
  },
  notification: {
    list: [],
  },
  dialog: {
    list: [],
  },
  country: {
    all: [],
    known: [],
  },
  list: {
    needRefresh: false,
    lastParams: {},
  },
  pageSize: {},
  auth: {
    error: null,
    loading: false,
    loggedIn: false,
    loggedByMagicLink: false,
    token: null,
    signUp: null, /* OK | WAITING_EMAIL_VALIDATION | INVALID | OK_NO_VALIDATION | USED | EXPIRED */
    // Object conforming to authShape.recoverPassword to avoid PropTypes warnings
    // Each property is initialized to false as no recovery action is in progress at startup
    recoverPassword: {
      post: false,        // Indicates if a recovery request has been sent
      getLink: false,     // Indicates if a recovery link has been generated
      updateLink: false,  // Indicates if the password has been updated via the link
    },
  },
  me: {
    error: null,
    loading: false,
    logout: false,
    data: null,
    update: {
      error: null,
      updated: false,
    },
    deleteMyPaymentMethod: {
      error: null,
      deleted: false,
    },
    features: null,
    isAdmin: false,
  },
};

// ------------------------------------- //
// Specific product reducers config
// ------------------------------------- //
const productsReducersConfig = {
  // OPAC
  [mProduct.list[0].value]: {
    autoComplete: {
      installations: {
        loading: false,
        elements: [],
        pagination: null,
        error: null,
      },
      devices: {
        loading: false,
        elements: [],
        pagination: null,
        error: null,
      },
      customers: {
        loading: false,
        elements: [],
        pagination: null,
        error: null,
      },
      simcards: {
        loading: false,
        elements: [],
        pagination: null,
        error: null,
      },
      distributions: {
        loading: false,
        elements: [],
        pagination: null,
        error: null,
      },
      profiles: {
        loading: false,
        elements: [],
        pagination: null,
        error: null,
      },
    },
    listFilter: {
      installation: {
        loading: false,
        data: null,
      },
      device: {
        loading: false,
        data: null,
      },
      customer: {
        loading: false,
        data: null,
      },
      simcard: {
        loading: false,
        data: null,
      },
      distribution: {
        loading: false,
        data: null,
      },
      profile: {
        loading: false,
        data: null,
      },
      cpmCarpark: {
        loading: false,
        data: null,
      },
    },
    // Async Operations
    asyncOperations: {
      list: [],
    },
    // Devices data
    deviceData: {
      elements: [],
      withAllInfoElements: [],
      desynchronized: false,
    },
    // Device event
    deviceEvent: {
      list: [],
    },
    // Operators
    operator: {
      list: {
        error: null,
        loading: false,
        elements: null,
      },
    },
    // Equipments
    equipment: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
    },
    // Device models
    deviceModel: {
      list: {
        error: null,
        loading: false,
        elements: null,
      },
    },
    // Devices
    device: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      current: {
        error: null,
        loading: false,
        data: null,
        installation: null,
        loadingInstallation: false,
      },
      linkSimcard: {
        error: null,
        done: false,
      },
      sendCmd: {
        error: null,
        done: false,
        idDevice: null,
        asyncOperation: null,
      },
      sendSms: {
        error: null,
        done: false,
      },
      delete: {
        error: null,
        deleted: false,
      },
      update: {
        error: null,
        updated: false,
      },
      permissions: [],
    },
    deviceConfig: {
      update: {
        error: null,
        updated: false,
        idDevice: null,
        idConfig: null,
        asyncOperation: null,
      },
    },
    deviceLog: {
      list: {
        error: null,
        loading: false,
        desynchronized: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
    },
    deviceCmd: {
      list: [],
    },
    deviceSms: {
      list: {
        loading: false,
        error: null,
        elements: null,
      },
      getStatus: {
        error: null,
        done: false,
        idSms: null,
      },
    },
    deviceDistribution: {
      list: {
        loading: false,
        error: null,
        elements: null,
      },
    },
    // Device slaves
    deviceSlave: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      delete: {
        error: null,
        deleted: false,
      },
      update: {
        error: null,
        updated: false,
      },
    },
    // Installations
    installation: {
      cache: {
        equipments: false,
        doors: false,
        sources: false,
        devices: false,
        scenarios: false,
        plannings: false,
      },
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
        asyncOperation: null,
      },
      update: {
        error: null,
        updated: false,
        idInstallation: null,
        asyncOperation: null,
      },
      delete: {
        error: null,
        deleted: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      permissions: [],
    },
    installationEntity: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
      add: {
        error: null,
        added: false,
        asyncOperation: null,
      },
      delete: {
        error: null,
        deleted: false,
      },
      update: {
        error: null,
        updated: false,
        idEntity: null,
        asyncOperation: null,
      },
    },
    installationDoor: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
      add: {
        error: null,
        added: false,
      },
      delete: {
        error: null,
        deleted: false,
      },
      update: {
        error: null,
        updated: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
    },
    installationSource: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
      add: {
        error: null,
        added: false,
      },
      delete: {
        error: null,
        deleted: false,
      },
      update: {
        error: null,
        updated: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
    },
    installationDevice: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
      add: {
        error: null,
        added: false,
      },
      delete: {
        error: null,
        deleted: false,
      },
      update: {
        error: null,
        updated: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      replace: {
        error: null,
        replaced: false,
      },
    },
    installationDeviceSlave: {
      current: {
        error: null,
        loading: false,
        data: null,
      },
      update: {
        error: null,
        updated: false,
        idDevice: null,
        idSlave: null,
        asyncOperation: null,
      },
      add: {
        error: null,
        added: false,
        idDevice: null,
        idSlave: null,
        asyncOperation: null,
      },
      delete: {
        error: null,
        deleted: false,
        idDevice: null,
        idSlave: null,
      },
      replace: {
        error: null,
        replaced: false,
        idDevice: null,
        idNewSlave: null,
      },
    },
    installationScenario: {
      list: {
        error: null,
        elements: null,
        loading: false,
        desynchronized: false,
      },
      add: {
        error: null,
        added: false,
        asyncOperation: null,
      },
      update: {
        error: null,
        updated: false,
        idScenario: null,
        asyncOperation: null,
      },
      delete: {
        error: null,
        deleted: false,
        idScenario: null,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      duplicate: {
        data: null,
      },
      trigger: {
        error: null,
        done: false,
        idScenario: null,
        asyncOperation: null,
      },
    },
    installationCustomer: {
      list: {
        error: null,
        loading: false,
        desynchronized: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
        asyncOperation: null,
      },
      update: {
        error: null,
        idCustomer: null,
        updated: false,
        asyncOperation: null,
      },
      updateState: {
        error: null,
        idCustomer: null,
        updated: false,
        asyncOperation: null,
      },
      delete: {
        error: null,
        idCustomer: null,
        deleted: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      accessWays: {
        error: null,
        idCustomer: null,
        loading: false,
      },
      randomUniqueCode: {
        error: null,
        loading: false,
        code: null,
      },
      dailyCodes: {
        error: null,
        done: false,
        size: 0,
      },
      export: {
        error: null,
        done: false,
      },
    },
    installationCustomerAccessway: {
      list: {
        error: null,
        loading: false,
        elements: null,
      },
      add: {
        error: null,
        added: false,
        idCustomer: null,
        asyncOperation: null,
      },
      update: {
        error: null,
        updated: false,
        idCustomer: null,
        asyncOperation: null,
      },
      delete: {
        error: null,
        deleted: false,
        idCustomer: null,
        asyncOperation: null,
      },
      triggerRight: {
        error: null,
        done: false,
        asyncOperation: null,
      },
    },
    installationPlanning: {
      list: {
        error: null,
        elements: null,
        loading: false,
        desynchronized: false,
      },
      add: {
        error: null,
        added: false,
        asyncOperation: null,
      },
      update: {
        error: null,
        idPlanning: null,
        updated: false,
        asyncOperation: null,
      },
      delete: {
        error: null,
        idPlanning: null,
        deleted: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
    },
    installationLog: {
      list: {
        error: null,
        loading: false,
        desynchronized: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
    },
    installationTrigger: {
      list: [],
    },
    installationAsyncOperation: {
      list: {
        error: null,
        loading: false,
        desynchronized: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      history: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      installations: {},
    },
    // Simcards
    simcard: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
        nbAdded: 0,
      },
      import: {
        error: null,
        loading: false,
        result: null,
      },
      update: {
        error: null,
        updated: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      synchronize: {
        error: null,
        done: false,
      },
      activate: {
        error: null,
        done: false,
      },
      linkDevice: {
        error: null,
        done: false,
      },
    },
    simcardGroup: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      addApn: {
        error: null,
        added: false,
      },
      deleteApn: {
        error: null,
        deleted: false,
      },
    },
    apn: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
      },
    },
    // Companies
    company: {
      list: {
        error: null,
        elements: null,
        flattenElements: null,
        loading: false,
      },
      add: {
        error: null,
        added: false,
        parent: null,
      },
      delete: {
        error: null,
        deleted: false,
      },
      update: {
        error: null,
        updated: false,
        idCompany: null,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
    },
    // Users
    user: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
      },
      update: {
        error: null,
        updated: false,
      },
      delete: {
        error: null,
        deleted: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
    },
    // Profiles
    profile: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
      },
      update: {
        error: null,
        updated: false,
      },
      delete: {
        error: null,
        deleted: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
    },
    // Permissions
    permission: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
    },
    // Alerting
    alerting: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
    },
    // Production
    // Device batches
    deviceBatch: {
      list: {
        error: null,
        loading: false,
        elements: {
          data: null,
          totalEntries: 0,
        },
      },
      add: {
        error: null,
        added: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      update: {
        error: null,
        loading: false,
        updated: false,
      },
      archive: {
        error: null,
        done: false,
      },
    },
    // Updates
    // Distributions
    distribution: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      update: {
        error: null,
        updated: false,
      },
      delete: {
        error: null,
        deleted: false,
      },
      archive: {
        error: null,
        done: false,
      },
    },
    // Rollouts
    rollout: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      delete: {
        error: null,
        deleted: false,
      },
      devicesList: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
    },
  },
  // CPM manager
  [mProduct.list[1].value]: {
    // Operations
    cpmOperation: {
      list: [],
    },
    // Events
    cpmEvent: {
      list: [],
    },
    // Autocomplete
    cpmAutoComplete: {
      carparks: {
        elements: {
          ...defaultPaginatedListResult,
        },
        error: null,
        loading: false,
      },
      customers: {
        loading: false,
        elements: [],
        pagination: null,
        error: null,
      },
      parkings: {
        loading: false,
        elements: [],
        pagination: null,
        error: null,
      },
    },
    cpmListFilter: {
      customer: {
        loading: false,
        data: null,
      },
      parking: {
        loading: false,
        data: null,
      },
    },
    // Carparks
    cpmCarpark: {
      cache: {
        plannings: false,
        parkingCategories: false,
        priceGrids: false,
      },
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      listWorkspaceGroups: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      public: {
        error: null,
        loading: false,
        data: null,
      },
      update: {
        error: null,
        updated: false,
      },
      triggerDoor: {
        error: null,
        done: false,
        result: null,
      },
      uniqueKeypadCode: {
        error: null,
        loading: false,
        result: null,
      },
      revenue: {
        day: {
          loading: false,
          value: null,
        },
        month: {
          loading: false,
          value: null,
        },
        year: {
          loading: false,
          value: null,
        },
      },
      synchronizationStatuses: {
        data: null,
        error: null,
        loading: false,
      },
      updateOnDemandSettings: {
        error: null,
        updated: false,
        result: null,
      },
      updateLicensePlateSettings: {
        error: null,
        updated: false,
      },
      updateBookingSettings: {
        error: null,
        updated: false,
        result: null,
      },
      updateGeneralSettings: {
        error: null,
        updated: false,
        result: null,
      },
      updateSpacesSettings: {
        error: null,
        updated: false,
        result: null,
      },
      updateFinancialInformation: {
        error: null,
        updated: false,
      },
      updateWorkspaceGroups: {
        error: null,
        updated: false,
      },
      defaultSlug: {
        error: null,
        loading: false,
        result: null,
      },
    },
    cpmCarparkTrigger: {
      list: [],
    },
    cpmCarparkActivity: {
      arrivals: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      departures: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      entries: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      exits: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
    },
    cpmCarparkPlanning: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
      add: {
        added: false,
        error: null,
        result: null,
      },
      update: {
        updated: false,
        error: null,
        result: null,
      },
      delete: {
        deleted: false,
        error: null,
        result: null,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
    },
    cpmCarparkParkingCategory: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
      add: {
        added: false,
        error: null,
        result: null,
      },
      update: {
        updated: false,
        error: null,
        result: null,
      },
      delete: {
        deleted: false,
        error: null,
        result: null,
      },
      addPriceGrid: {
        added: false,
        error: null,
        result: null,
      },
      deletePriceGrid: {
        deleted: false,
        error: null,
        result: null,
      },
      sendPriceGridToCustomer: {
        sent: false,
        error: null,
      },
    },
    cpmCarparkPriceGrid: {
      list: {
        error: null,
        elements: null,
        loading: false,
      },
      add: {
        added: false,
        error: null,
        result: null,
      },
      update: {
        updated: false,
        error: null,
        result: null,
      },
    },
    cpmCarparkCustomer: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        added: false,
        error: null,
        result: null,
      },
      update: {
        updated: false,
        error: null,
        result: null,
      },
      delete: {
        deleted: false,
        error: null,
        result: null,
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      createAccount: {
        done: false,
        error: null,
        result: null,
      },
      deleteAccount: {
        done: false,
        error: null,
        result: null,
      },
      parkings: {},
      bookings: {},
      oldParkings: {},
      accesses: {},
    },
    cpmCarparkParking: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        added: false,
        error: null,
        result: null,
      },
      update: {
        updated: false,
        error: null,
        result: null,
      },
      delete: {
        deleted: false,
        error: null,
        result: null,
      },
      accesses: {},
    },
    cpmCarparkOnDemand: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      categorize: {
        done: false,
        error: null,
        result: null,
      },
      add: {
        added: false,
        error: null,
      },
      delete: {
        deleted: false,
        error: null,
        result: null,
      },
      refund: {
        refunded: false,
        error: null,
      },
      terminate: {
        error: null,
        terminated: false,
      },
      update: {
        updated: false,
        error: null,
      },
    },
    cpmCarparkAccess: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
    },
    cpmCarparkMetric: {
      occupancy: {
        live: {
          error: null,
          loading: false,
          global: null,
          parkingCategories: [],
        },
        period: {
          error: null,
          loading: false,
          global: null,
          parkingCategories: [],
        },
        predicted: {
          error: null,
          loading: false,
          global: null,
          parkingCategories: [],
        },
      },
      access: {
        period: {
          error: null,
          loading: false,
          global: null,
          parkingCategories: [],
        },
      },
      revenue: {
        period: {
          error: null,
          loading: false,
          global: null,
          parkingCategories: [],
        },
        report: {
          error: null,
          loading: false,
          uri: null,
        },
      },
    },
    cpmCarparkBooking: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      listOnepark: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        added: false,
        error: null,
        result: null,
      },
      cancel: {
        cancelled: false,
        error: null,
        result: null,
      },
      search: {
        error: null,
        loading: false,
        result: null,
      },
      update: {
        updated: false,
        error: null,
        result: null,
      },
      refund: {
        loading: false,
        error: null,
        refunded: null,
      },
      sendPaymentLink: {
        loading: false,
        error: null,
        result: null,
      },
      resendBookingConfirmationEmail: {
        loading: false,
        error: null,
        result: null,
      },
    },
    cpmCarparkInvoice: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      downloadOne: {
        error: null,
        downloaded: false,
        downloadRequested: false,
        result: null,
      },
      downloadMultiple: {
        error: null,
        downloaded: false,
        result: null,
      },
      update: {
        error: null,
        updated: false,
      },
    },
    cpmSidebar: {
      isCpmSidebarOpen: false,
    },
    cpmAccount: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      setStatus: {
        done: false,
        error: null,
        result: null,
      },
    },
    cpmPartner: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
      },
      updateFinancialInformation: {
        updated: false,
        error: null,
        result: null,
      },
    },
    cpmWorkspace: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      get: {
        error: null,
        loading: false,
        result: null,
      },
      add: {
        error: null,
        added: false,
      },
      update: {
        error: null,
        updated: false,
      },
    },
    cpmWorkspaceGroup: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      get: {
        error: null,
        loading: false,
        result: null,
      },
      add: {
        error: null,
        added: false,
      },
      update: {
        error: null,
        updated: false,
      },
    },
    cpmSlug: {
      current: {
        error: null,
        loading: false,
        result: null,
      },
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      add: {
        error: null,
        added: false,
      },
      delete: {
        error: null,
        deleted: false,
      },
      update: {
        error: null,
        updated: false,
      },
    },
    cpmFinance: {
      purchase: {
        error: null,
        loading: false,
        result: null,
        refunded: false,
      },
    },
  },
  // CPM client
  [mProduct.list[2].value]: {
    // Operations
    cpmOperation: {
      list: [],
    },
    // Events
    cpmEvent: {
      list: [],
    },
    // Carpark
    cpmCarpark: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      current: {
        error: null,
        loading: false,
        data: null,
      },
      public: {
        error: null,
        loading: false,
        data: null,
      },
    },
    // Booking
    cpmBooking: {
      list: {
        error: null,
        loading: false,
        elements: {
          ...defaultPaginatedListResult,
        },
      },
      listMyBookingOptions: {
        error: null,
        loading: false,
        result: null,
      },
      book: {
        error: null,
        loading: false,
        result: null,
      },
      cancel: {
        cancelled: false,
        error: null,
        result: null,
      },
      declareFinalExit: {
        loading: false,
        error: null,
        result: null,
      },
      downloadMyInvoice: {
        error: null,
        downloaded: false,
        result: null,
      },
      myBooking: {
        error: null,
        loading: false,
        result: null,
        updated: {
          error: null,
          loading: false,
          result: null,
        },
      },
      purchase: {
        error: null,
        loading: false,
        result: null,
      },
      offer: {
        error: null,
        loading: false,
        result: null,
      },
      search: {
        error: null,
        loading: false,
        result: null,
      },
      searchBookingExtensionOffer: {
        error: null,
        loading: false,
        result: null,
      },
    },
    // Finance
    cpmFinance: {
      purchase: {
        error: null,
        loading: false,
        result: null,
      },
    },
    // On-demand
    cpmOnDemand: {
      get: {
        error: null,
        loading: false,
        result: null,
      },
      search: {
        error: null,
        loading: false,
        result: null,
      },
      checkout: {
        done: false,
        error: null,
        result: null,
      },
      extend: {
        done: false,
        error: null,
        result: null,
      },
      overstay: {
        done: false,
        error: null,
        result: null,
      },
      update: {
        error: null,
        updated: false,
      },
      refund: {
        error: null,
        refunded: false,
      },
      declareFinalExit: {
        done: false,
        error: null,
      },
      result: null,
    },
    // Slug
    cpmSlug: {
      current: {
        error: null,
        loading: false,
        result: null,
      },
    },
  },
};

// ------------------------------------- //
// Combine all configs
// ------------------------------------- //
const combinedConfig = {
  ...staticConfig,
  ...commonReducersConfig,
  ...productsReducersConfig[PRODUCT],
};

module.exports = combinedConfig;



// WEBPACK FOOTER //
// ./constants/Config.js